import React, { useState, useReducer, useRef, useEffect } from 'react'
import InvisibleRecaptcha from 'src/components/InvisibleRecaptcha'

import Form from './Form'

import csrfHeaders from 'src/utils/csrfHeaders'
import { offerPasskeySignIn } from 'src/passkeys/signInPage'

const reducer = (state, action) => {
  switch (action.type) {
    case 'setFieldValue':
      return { ...state, [action.field]: action.value }
    default:
      throw new Error('Invalid reducer action')
  }
}

const App = props => {
  const [ loading, setLoading ] = useState(false)
  const [ action, setAction ] = useState(props.urls.signinup)
  // signinError is HTML the server has already escaped or sanitized.
  const [ errors, setErrors ] = useState(() =>
    props.signinError
      ? { ...props.errors, base: [ props.signinError, ...(props.errors?.base || []) ] }
      : props.errors
  )
  const [ resend, setResend ] = useState(null)
  // Whether the account typed in has a passkey: null until it's looked up.
  const [ accountHasPasskey, setAccountHasPasskey ] = useState(null)

  const [ user, dispatch ] = useReducer(reducer, {
    ...props.user,
    password: '',
    password_confirmation: '',
  })

  const recaptcha = useRef()
  const resetPasswordTimeout = useRef()
  const mounted = useRef()

  useEffect(() => {
    if (mounted.current) return

    const interval = setInterval(() => {
      if (document.querySelector('#password:-webkit-autofill')) {
        setAction(props.urls.signin)
        clearInterval(interval)
      }
    }, 10)
    setTimeout(() => clearInterval(interval), 500)
  }, [])

  useEffect(() => {
    if (props.user.email && !mounted.current) {
      const timeout = setTimeout(submitForm, 10)
      return () => clearTimeout(timeout)
    }
  }, [ props.user.email ])

  useEffect(() => {
    mounted.current = true
    return () => mounted.current = false
  }, [])

  useEffect(() => {
    offerPasskeySignIn(accountHasPasskey)
  }, [ accountHasPasskey ])

  const baseProps = id => ({ id, name: `user[${id}]`, error: errors?.[id]?.[0] })

  const inputProps = id => ({
    ...baseProps(id),
    value: user[id] || '',
    onChange: e => {
      setErrors(null)
      dispatch({ type: 'setFieldValue', field: id, value: e.target.value })
    }
  })

  const booleanProps = id => ({
    ...baseProps(id),
    checked: user[id] || false,
    value: '1',
    onChange: e =>
      dispatch({ type: 'setFieldValue', field: id, value: e.target.checked })
  })

  const handleEmailChange = e => {
    inputProps('email').onChange(e)
    setAccountHasPasskey(null)
    resetPasswordTimeout.current = setTimeout(() => {
      setAction(props.urls.signinup)
      if (action != props.urls.signup)
        dispatch({ type: 'setFieldValue', field: 'password', value: '' })
    }, 50)
  }

  const handleSignInPasswordChange = e => {
    inputProps('password').onChange(e)
    setAction(props.urls.signin)
    setTimeout(() => clearTimeout(resetPasswordTimeout.current), 20)
  }

  const submitForm = token => {
    if (loading) return
    setLoading(true)

    const params = {
      user,
      'g-recaptcha-response': token,
      source: props.source,
      cobrand: props.cobrand,
    }

    const success = ({ action, data, passkey }) => {
      if (recaptcha.current)
        recaptcha.current.reset()

      switch (action) {
        case 'next_step':
          setErrors(null)
          setAction(props.urls[data])
          setAccountHasPasskey(data == 'signin' && !!passkey)
          return
        case 'redirect':
          return location.href = data
        default:
          throw new Error('Invalid success server response')
      }
    }

    fetch(action, {
      method: 'POST',
      headers: csrfHeaders(),
      body: JSON.stringify(params),
    }).then(response => response.json().then(json => {
      if (response.ok)
        success(json)
      else {
        setResend(json.resend)
        setErrors(json.errors)
      }
    }).catch(error => {
      console.error(error)
      setErrors({
        base: [ 'Something went wrong. Please reload the page and try again' ]
      })
    })).catch(error => {
      console.error(error)
      setErrors({
        base: [ 'There was a network error. Please try again' ]
      })
    }).finally(() => {
      setLoading(false)
      recaptcha.current?.reset()
    })
  }

  const handleSubmit = e => {
    e.preventDefault()
    if (recaptcha.current)
      recaptcha.current.execute()
    else
      submitForm()
  }

  return (
    <Form
      {...props}
      user={user}
      loading={loading}
      resend={resend}
      action={action}
      errors={errors}
      baseProps={baseProps}
      inputProps={inputProps}
      booleanProps={booleanProps}
      handleEmailChange={handleEmailChange}
      handleSignInPasswordChange={handleSignInPasswordChange}
      handleSubmit={handleSubmit}
    >
      { action == props.urls.signup ?
        <div className="position-relative" style={{ zIndex: 10 }}>
          <InvisibleRecaptcha
            ref={recaptcha}
            sitekey={props.recaptchaSiteKey}
            onChange={submitForm}
          />
        </div>
        : null
      }
    </Form>
  )
}

export default App
